/**
 * @file: PlanDetailActionsButton.tsx
 *
 * Three-dots actions menu for a single maintenance-plan detail, shared by
 * the maintenance-plan feed body and the maintenance-plans timeline page.
 * Tapping opens an action sheet — toggle in-store / customer / all alerts,
 * cancel the plan detail, or open the in-place edit bottom sheet. Local
 * state tracks the toggled flags so labels flip immediately after a
 * successful API call; `onEdited` bubbles a successful edit back to the
 * parent so it can patch its own snapshot/row, and `onRemoved` lets the
 * parent drop the row after a cancel.
 *
 * Both call sites key the maintenance-plan endpoints by `planNo`, so the
 * detail shape only needs the small editable surface below — the feed
 * snapshot and the denormalized list row both satisfy it.
 */

import { MaintenancePlanBottomSheet } from '@euler/app/flows/task/pages/finish-task/components/MaintenancePlanBottomSheet';
import {
  INSURANCE_EXPIRY_ITEM_NAME,
  MaintenancePlanListItem,
} from '@euler/app/flows/task/pages/finish-task/types';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { MaintenanceServicePlanDetailType } from '@euler/model/enum/MaintenanceServicePlanDetailType';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import { TouchableOpacity } from 'react-native';

/** Fields the actions menu reads off a plan detail (snapshot or list row). */
export interface EditablePlanDetail {
  id: number;
  name: string;
  type: MaintenanceServicePlanDetailType;
  nextServiceDate: string | Date;
  nextServiceMileage: number;
  remark?: string | null;
  isAlertEnabled: boolean;
  isAlertEnabledForCustomer: boolean;
}

export interface PlanDetailEditPatch {
  name?: string;
  nextServiceDate?: string;
  nextServiceMileage?: number;
  remark?: string;
}

export const PlanDetailActionsButton: FC<{
  planNo: string;
  planDetailId: number;
  detail: EditablePlanDetail;
  onEdited?: (patch: PlanDetailEditPatch) => void;
  onRemoved?: () => void;
  /** Fires after a successful alert toggle with the full new flag pair, so the
   * parent can refresh its own view (e.g. the bell indicators). */
  onAlertChanged?: (settings: {
    isAlertEnabled: boolean;
    isAlertEnabledForCustomer: boolean;
  }) => void;
}> = memo(
  ({ planNo, planDetailId, detail, onEdited, onRemoved, onAlertChanged }) => {
    const { rpx } = useResponsiveContext();
    const { maintenancePlanService } = useServiceFactory();
    const { showActionSheetWithOptions } = useActionSheet();
    const confirm = useConfirm();

    const [alertEnabled, setAlertEnabled] = useState(detail.isAlertEnabled);
    const [alertEnabledForCustomer, setAlertEnabledForCustomer] = useState(
      detail.isAlertEnabledForCustomer,
    );
    const [removed, setRemoved] = useState(false);
    const [editing, setEditing] = useState(false);

    type ActionType =
      | 'edit'
      | 'toggle-all'
      | 'toggle-store'
      | 'toggle-customer'
      | 'cancel-detail'
      | 'cancel';

    const actions = useMemo<{ type: ActionType; label: string }[]>(() => {
      const items: { type: ActionType; label: string }[] = [];
      items.push({ type: 'edit', label: '修改保养计划' });
      const allEnabled = alertEnabled || alertEnabledForCustomer;
      items.push({
        type: 'toggle-all',
        label: allEnabled ? '关闭所有提醒' : '开启所有提醒',
      });
      items.push({
        type: 'toggle-store',
        label: alertEnabled ? '关闭门店提醒' : '开启门店提醒',
      });
      items.push({
        type: 'toggle-customer',
        label: alertEnabledForCustomer ? '关闭客户提醒' : '开启客户提醒',
      });
      items.push({ type: 'cancel-detail', label: '取消保养计划' });
      items.push({ type: 'cancel', label: '返回' });
      return items;
    }, [alertEnabled, alertEnabledForCustomer]);

    const onEdit = useCallback(() => setEditing(true), []);
    const onEditDone = usePersistFn(
      async (values: {
        id: string;
        name: string;
        nextServiceDate: string | Date;
        nextServiceMileage: number | undefined;
        remark?: string;
      }) => {
        const nextServiceDateStr =
          values.nextServiceDate instanceof Date
            ? values.nextServiceDate.toISOString().slice(0, 10)
            : String(values.nextServiceDate).slice(0, 10);
        await maintenancePlanService.updateMaintenancePlanDetail(
          planNo,
          planDetailId,
          {
            name: values.name,
            nextServiceDate: values.nextServiceDate,
            nextServiceMileage: values.nextServiceMileage,
            remark: values.remark,
          },
        );
        setEditing(false);
        onEdited?.({
          name: values.name,
          nextServiceDate: nextServiceDateStr,
          nextServiceMileage: values.nextServiceMileage,
          remark: values.remark,
        });
      },
    );

    const onToggleAll = usePersistFn(async () => {
      const enabled = !(alertEnabled || alertEnabledForCustomer);
      if (
        !enabled &&
        !(await confirm({
          title: '确认',
          message: '是否确认关闭该保养计划的所有提醒?',
        }))
      ) {
        return;
      }
      await maintenancePlanService.updateMaintenancePlanDetailAlertSettings(
        planNo,
        planDetailId,
        { enabled, enabledForCustomer: enabled },
      );
      setAlertEnabled(enabled);
      setAlertEnabledForCustomer(enabled);
      onAlertChanged?.({
        isAlertEnabled: enabled,
        isAlertEnabledForCustomer: enabled,
      });
    });

    const onToggleStore = usePersistFn(async () => {
      const enabled = !alertEnabled;
      if (
        !enabled &&
        !(await confirm({
          title: '确认',
          message: '是否确认关闭该保养计划的门店提醒?',
        }))
      ) {
        return;
      }
      await maintenancePlanService.updateMaintenancePlanDetailAlertSettings(
        planNo,
        planDetailId,
        { enabled },
      );
      setAlertEnabled(enabled);
      onAlertChanged?.({
        isAlertEnabled: enabled,
        isAlertEnabledForCustomer: alertEnabledForCustomer,
      });
    });

    const onToggleCustomer = usePersistFn(async () => {
      const enabled = !alertEnabledForCustomer;
      if (
        !enabled &&
        !(await confirm({
          title: '确认',
          message: '是否确认关闭该保养计划的客户提醒?',
        }))
      ) {
        return;
      }
      await maintenancePlanService.updateMaintenancePlanDetailAlertSettings(
        planNo,
        planDetailId,
        { enabledForCustomer: enabled },
      );
      setAlertEnabledForCustomer(enabled);
      onAlertChanged?.({
        isAlertEnabled: alertEnabled,
        isAlertEnabledForCustomer: enabled,
      });
    });

    const onCancelDetail = usePersistFn(async () => {
      if (
        !(await confirm({
          title: '确认',
          message: '您即将删除该保养计划，该操作不可撤销，是否继续?',
        }))
      ) {
        return;
      }
      await maintenancePlanService.removeMaintenancePlanDetail(
        planNo,
        planDetailId,
      );
      setRemoved(true);
      onRemoved?.();
    });

    const onPress = useCallback(() => {
      showActionSheetWithOptions(
        {
          options: actions.map(a => a.label),
          cancelButtonIndex: actions.findIndex(a => a.type === 'cancel'),
          destructiveButtonIndex: actions.findIndex(
            a => a.type === 'cancel-detail',
          ),
        },
        buttonIndex => {
          if (buttonIndex == null || buttonIndex < 0) return;
          const action = actions[buttonIndex];
          switch (action.type) {
            case 'edit':
              onEdit();
              break;
            case 'toggle-all':
              void onToggleAll();
              break;
            case 'toggle-store':
              void onToggleStore();
              break;
            case 'toggle-customer':
              void onToggleCustomer();
              break;
            case 'cancel-detail':
              void onCancelDetail();
              break;
            default:
              break;
          }
        },
      );
    }, [
      actions,
      onCancelDetail,
      onEdit,
      onToggleAll,
      onToggleCustomer,
      onToggleStore,
      showActionSheetWithOptions,
    ]);

    const editTarget = useMemo(
      () => buildPlanListItemFromDetail(detail),
      [detail],
    );

    if (removed) return null;
    return (
      <>
        <TouchableOpacity
          onPress={onPress}
          activeOpacity={0.6}
          hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
        >
          {/* Icomoon ships only `more-vertical`; rotate it 90° for the
            horizontal-dots look. */}
          <Icon
            name="more-vertical"
            size={rpx(18)}
            color="#6b7280"
            style={{ transform: [{ rotate: '90deg' }] }}
          />
        </TouchableOpacity>
        <MaintenancePlanBottomSheet
          show={editing}
          target={editTarget}
          defaultNextServiceDate={detail.nextServiceDate}
          defaultNextServiceMileage={detail.nextServiceMileage}
          onClose={() => setEditing(false)}
          onDone={onEditDone}
        />
      </>
    );
  },
);

/**
 * Adapt a plan detail (feed snapshot or list row) to the
 * `MaintenancePlanListItem` shape the shared bottom sheet expects. The
 * sheet only reads `id`, `name`, `type`, and `planInfo` off the target, so
 * the unused per-variant fields get harmless stubs.
 */
function buildPlanListItemFromDetail(
  detail: EditablePlanDetail,
): MaintenancePlanListItem {
  const baseItem = {
    id: `__plan_detail:${detail.id}__`,
    name: detail.name,
    remark: detail.remark ?? undefined,
    planInfo: {
      nextServiceDate: detail.nextServiceDate,
      nextServiceMileage: detail.nextServiceMileage,
      remark: detail.remark ?? undefined,
    },
  };
  switch (detail.type) {
    case MaintenanceServicePlanDetailType.ConstructionJob:
      return { type: 'construction-job', ...baseItem, jobId: 0, author: '' };
    case MaintenanceServicePlanDetailType.InspectionSubject:
      return {
        type: 'inspection-subject',
        ...baseItem,
        flowId: '',
        author: '',
      };
    case MaintenanceServicePlanDetailType.FaultLikeSubject:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return { type: 'fault', ...baseItem, faultInspectionItem: {} as any };
    case MaintenanceServicePlanDetailType.PendingIssue:
    case MaintenanceServicePlanDetailType.SolvedPendingIssue:
      return {
        type: 'pending-issue',
        ...baseItem,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        issue: {} as any,
        repairConfirmed:
          detail.type === MaintenanceServicePlanDetailType.SolvedPendingIssue,
      };
    case MaintenanceServicePlanDetailType.Custom:
      return { type: 'custom', ...baseItem };
    case MaintenanceServicePlanDetailType.InsuranceExpiry:
      return {
        type: 'insurance-expiry',
        ...baseItem,
        name: INSURANCE_EXPIRY_ITEM_NAME,
        optedIn: true,
      };
    default:
      return { type: 'custom', ...baseItem };
  }
}
